import { useEffect, useState } from 'react';
import { Button, Input, InputNumber, Modal, Select, Space, Switch, Typography } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import type { FieldMeta } from '../../../api/types';
import type { FreeTableSection } from '../../../contract/types';
import { insertRow, normalizeFreeTable } from '../../../contract/freeTable';
import { loadFieldMetas } from '../../../contract/linkedMeta';
import { INDEX_REF } from '../../../../render-core/renderer';
import { FreeTableGrid } from './FreeTableGrid';

interface Props {
  section: FreeTableSection;
  onChange: (s: FreeTableSection) => void;
  fieldMetas: FieldMeta[];
}

/**
 * 侧栏里只放摘要与整表属性；行列/合并这类操作需要横向空间，走弹窗里的
 * FreeTableGrid（弹窗挂在 body 上，侧边栏和全屏编辑器都够用）。
 */
/** 关联字段类型：单向 / 双向。 */
const LINK_TYPES = [18, 21];

export function FreeTableEditor({ section, onChange, fieldMetas }: Props) {
  const [open, setOpen] = useState(false);
  const t = normalizeFreeTable(section);
  const linkFields = fieldMetas.filter((m) => LINK_TYPES.includes(m.type));

  const patchRepeat = (patch: Partial<NonNullable<FreeTableSection['repeat']>>) => {
    if (!section.repeat) return;
    onChange({ ...section, repeat: { ...section.repeat, ...patch } });
  };

  // 选了关联字段就把对方表的字段列出来，供每一列挑
  const [linkedFieldMetas, setLinkedFieldMetas] = useState<FieldMeta[]>([]);
  // 列绑哪张表的字段，取决于数据来源：关联子表用对方表，本表多记录用本表
  const columnSourceFields =
    (section.repeat?.source ?? 'link') === 'records' ? fieldMetas : linkedFieldMetas;
  useEffect(() => {
    const ref = section.repeat?.source === 'records' ? '' : section.repeat?.linkFieldId;
    if (!ref) {
      setLinkedFieldMetas([]);
      return;
    }
    const meta = fieldMetas.find((m) => m.id === ref || m.name === ref);
    const tableId = (meta as unknown as { property?: { tableId?: string } } | undefined)?.property
      ?.tableId;
    if (!tableId) {
      setLinkedFieldMetas([]);
      return;
    }
    let cancelled = false;
    loadFieldMetas(tableId)
      .then((metas) => {
        if (!cancelled) setLinkedFieldMetas(metas);
      })
      .catch(() => {
        if (!cancelled) setLinkedFieldMetas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [section.repeat?.linkFieldId, fieldMetas]);

  return (
    <div>
      <Space size={8} wrap style={{ marginBottom: 8 }}>
        <Button size="small" type="primary" icon={<TableOutlined />} onClick={() => setOpen(true)}>
          编辑表格
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t.rows.length} 行 × {t.colWidths.length} 列
        </Typography.Text>
        <Button size="small" onClick={() => onChange(insertRow(t, t.rows.length))}>
          追加一行
        </Button>
      </Space>

      <Space size={12} wrap align="start">
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>框线(px)</Typography.Text>
          <br />
          <InputNumber
            size="small"
            min={0}
            max={4}
            value={section.borderPx}
            onChange={(v) => onChange({ ...section, borderPx: v ?? 1 })}
            style={{ width: 72 }}
          />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>字号(px)</Typography.Text>
          <br />
          <InputNumber
            size="small"
            min={6}
            max={28}
            value={section.fontSizePx}
            onChange={(v) => onChange({ ...section, fontSizePx: v })}
            placeholder="默认"
            style={{ width: 72 }}
          />
        </div>
      </Space>

      {/* 动态明细：让某一行随关联子表自动加行。
          有了它，「左边一个跨行的合并大格 + 右边随子表加行的明细」第一次能
          在同一张表里成立 —— 明细表格能带子表但不能合并，自由表格能合并但
          只能填主记录字段，客户要的排法两边都做不到。 */}
      <div style={{ borderTop: '1px dashed var(--border-soft)', marginTop: 10, paddingTop: 8 }}>
        <Space size={6} wrap>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>数据来源</Typography.Text>
          <Select
            size="small"
            style={{ width: 190 }}
            value={section.repeat ? section.repeat.source ?? 'link' : 'static'}
            onChange={(v: 'static' | 'link' | 'records') => {
              if (v === 'static') {
                onChange({ ...section, repeat: null });
                return;
              }
              onChange({
                ...section,
                repeat: {
                  ...(section.repeat ?? {
                    linkFieldId: '',
                    rowIndex: Math.min(1, Math.max(0, t.rows.length - 1)),
                    columnFields: {},
                  }),
                  source: v,
                  ...(v === 'link' && !section.repeat?.linkFieldId
                    ? { linkFieldId: linkFields[0]?.id ?? '' }
                    : {}),
                },
              });
            }}
            options={[
              { label: '静态表（行数固定）', value: 'static' },
              { label: '动态明细 · 关联子表', value: 'link' },
              { label: '动态明细 · 本表多条记录', value: 'records' },
            ]}
          />
        </Space>

        {section.repeat && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Space size={6} wrap>
              {(section.repeat.source ?? 'link') === 'link' ? (
                <>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>关联字段</Typography.Text>
                  <Select
                    size="small"
                    style={{ width: 150 }}
                    placeholder="选择关联字段"
                    value={section.repeat.linkFieldId || undefined}
                    onChange={(v) => patchRepeat({ linkFieldId: v, columnFields: {} })}
                    options={linkFields.map((m) => ({ label: m.name, value: m.id }))}
                  />
                </>
              ) : (
                <>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>记录范围</Typography.Text>
                  <Select
                    size="small"
                    style={{ width: 120 }}
                    value={section.repeat.recordRange ?? 'visible'}
                    onChange={(v) => patchRepeat({ recordRange: v })}
                    options={[
                      { label: '视图可见', value: 'visible' },
                      { label: '视图选中', value: 'selected' },
                      { label: '全表', value: 'all' },
                    ]}
                  />
                </>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>样板行</Typography.Text>
              <Select
                size="small"
                style={{ width: 96 }}
                value={section.repeat.rowIndex}
                onChange={(v) => patchRepeat({ rowIndex: v })}
                options={t.rows.map((_, i) => ({ label: `第 ${i + 1} 行`, value: i }))}
              />
            </Space>

            {columnSourceFields.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  每列取子表的哪个字段（不选则保留样板里的文字）
                </Typography.Text>
                {t.colWidths.map((_, ci) => (
                  <Space key={ci} size={6}>
                    <Typography.Text style={{ fontSize: 11, width: 44 }}>第 {ci + 1} 列</Typography.Text>
                    <Select
                      size="small"
                      style={{ width: 150 }}
                      allowClear
                      placeholder="保留原文字"
                      value={section.repeat?.columnFields?.[ci]}
                      onChange={(v) => {
                        const next = { ...(section.repeat?.columnFields ?? {}) };
                        if (v) next[ci] = v;
                        else delete next[ci];
                        patchRepeat({ columnFields: next });
                      }}
                      options={[
                        { label: '序号（自动 1、2、3…）', value: INDEX_REF },
                        ...columnSourceFields.map((m) => ({ label: m.name, value: m.id })),
                      ]}
                    />
                  </Space>
                ))}
              </div>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {(section.repeat.source ?? 'link') === 'link'
                  ? section.repeat.linkFieldId
                    ? '正在读取关联表字段…'
                    : '先选一个关联字段'
                  : '当前表还没有字段'}
              </Typography.Text>
            )}
            <Space size={10} wrap>
              <Space size={4}>
                <Switch
                  size="small"
                  checked={!!section.summary}
                  onChange={(v) =>
                    onChange({ ...section, summary: v ? { label: '合计', columns: [] } : null })
                  }
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>合计行</Typography.Text>
              </Space>
              <Space size={4}>
                <Switch
                  size="small"
                  checked={!!section.zebra}
                  onChange={(v) => onChange({ ...section, zebra: v })}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>斑马纹</Typography.Text>
              </Space>
              <Space size={4}>
                <Switch
                  size="small"
                  checked={!!section.showCount}
                  onChange={(v) => onChange({ ...section, showCount: v })}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>末尾「共 N 条」</Typography.Text>
              </Space>
            </Space>

            {section.summary && (
              <Space size={6} wrap>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>合计标签</Typography.Text>
                <Input
                  size="small"
                  style={{ width: 80 }}
                  value={section.summary.label}
                  onChange={(e) =>
                    onChange({ ...section, summary: { ...section.summary!, label: e.target.value } })
                  }
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>求和列</Typography.Text>
                <Select
                  size="small"
                  mode="multiple"
                  style={{ minWidth: 150, flex: 1 }}
                  placeholder="选要加总的列"
                  value={section.summary.columns}
                  onChange={(cols: number[]) =>
                    onChange({ ...section, summary: { ...section.summary!, columns: cols } })
                  }
                  options={t.colWidths.map((_, ci) => ({ label: `第 ${ci + 1} 列`, value: ci }))}
                />
              </Space>
            )}
          </div>
        )}
      </div>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="编辑表格"
        width="min(1080px, 96vw)"
        footer={<Button type="primary" onClick={() => setOpen(false)}>完成</Button>}
        destroyOnClose
      >
        <FreeTableGrid section={section} onChange={onChange} fieldMetas={fieldMetas} />
      </Modal>
    </div>
  );
}
